import React from 'react';
import { API } from '../api';
import { Field, Section, Segmented } from './ui';
import { EventForm, BLANK_EVENT } from './EventsTab';
import { nzHolidayMap } from '../utils/holidays';
import { occurrencesBetween, seriesEnd } from '../utils/eventRecurrence';

/*
 * The club calendar: everything on at the club, on a month grid.
 *
 * It stores nothing of its own. It reads the events and competitions that
 * already exist, and where it writes, it writes to them — adding an event
 * here creates the same record the Events tab does, and the New competition
 * button hands the clicked day to the setup screen. Duplicating either into
 * a calendar of its own would give a club two places to change a date and
 * one of them wrong.
 *
 * A competition is marked on the days it is played — one per round, plus
 * each final that has a date — and not across its start-to-end span. A six
 * week series played on Wednesdays is six marks, not forty-two: colouring
 * in the whole span says the club is busy every day for a month and a half,
 * which is wrong and hides the very gaps the chart is read for. A comp with
 * nothing scheduled falls back to its span, and one with no dates at all
 * contributes nothing rather than landing on today.
 *
 * Admin's view, so nothing is hidden: unpublished and cancelled events are
 * dimmed and labelled, not dropped. That is the whole point of looking at
 * the month before the newsletter goes out.
 *
 * Two views over the same marks. The month grid answers "what is on in
 * May"; the planner — a row per month, week-aligned, the shape of the chart
 * on a club noticeboard — answers "what does the season look like", which
 * is the question asked once a year and never answerable by paging through
 * twelve months one at a time. Its range is a pair of months rather than a
 * calendar year, because a bowls year runs July to June and a chart that
 * cuts it in half at Christmas is not a chart of anything. Both views feed
 * the same day list underneath, so picking a day means the same thing in
 * either.
 */

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_INITIALS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
/* Thirty-one days, plus the six of slack a month starting on a Sunday
   needs before its 1st, is the widest a week-aligned row can be. */
const WEEK_COLUMNS = Array.from({ length: 37 }, (_, i) => i);

/* How long a competition can run before it stops being "on" for the whole
   of it. A weekend championship is on all weekend; a six-week Wednesday
   series is on for six Wednesdays, and colouring in the weeks between is
   the thing playingDays exists to prevent. Four days covers a long weekend,
   which is the longest a club plays straight through. */
const CONTINUOUS_RUN_DAYS = 4;

const iso = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayISO = () => iso(new Date());
const dayOnly = v => (v ? String(v).slice(0, 10) : '');

/* Monday-first: JS gives Sunday as 0, and a club's week does not start on a
   Sunday. */
const mondayIndex = d => (d.getDay() + 6) % 7;

/* The planner's range is held as the "YYYY-MM" an <input type="month">
   gives back, so what is on screen and what is in the box are the same
   value with nothing to convert between them. */
const monthKey = (year, month) => `${year}-${String(month + 1).padStart(2, '0')}`;
const parseMonthKey = key => {
  const [y, m] = String(key || '').split('-').map(Number);
  return (y && m) ? { year: y, month: m - 1 } : null;
};
const monthKeyLabel = key => {
  const p = parseMonthKey(key);
  return p ? new Date(p.year, p.month, 1).toLocaleDateString('en-NZ', { month: 'short', year: 'numeric' }) : '';
};
/** Add months to a "YYYY-MM", letting Date carry the year over. */
const shiftMonthKey = (key, by) => {
  const p = parseMonthKey(key);
  if (!p) return key;
  const d = new Date(p.year, p.month + by, 1);
  return monthKey(d.getFullYear(), d.getMonth());
};

/**
 * The months a range covers, inclusive.
 *
 * Capped at two years, and a backwards range is read forwards: both are
 * things a pair of month boxes lets someone type, and neither is worth a
 * validation message when the honest answer is to show them something.
 */
const MAX_PLANNER_MONTHS = 24;
function monthsInRange(fromKey, toKey) {
  const from = parseMonthKey(fromKey);
  const to = parseMonthKey(toKey);
  if (!from) return [];
  if (!to) return [from];
  const a = from.year * 12 + from.month;
  const b = to.year * 12 + to.month;
  const start = Math.min(a, b);
  const count = Math.min(Math.abs(b - a) + 1, MAX_PLANNER_MONTHS);
  return Array.from({ length: count }, (_, i) => ({
    year: Math.floor((start + i) / 12),
    month: (start + i) % 12,
  }));
}

const fmtLongDate = isoStr => {
  const d = new Date(`${isoStr}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? isoStr
    : d.toLocaleDateString('en-NZ', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

/** Every ISO date from `from` to `to` inclusive, capped so a mistyped end
    date — the year 2202 — cannot build a million cells. */
function datesBetween(from, to) {
  if (!from) return [];
  if (!to || to <= from) return [from];
  const out = [];
  const d = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);
  while (d <= end && out.length < 400) {
    out.push(iso(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

/* A repeating event that runs more than a day runs the same number of days
   each time, so each occurrence needs its own last day. */
const addDaysISO = (from, n) => {
  const d = new Date(`${from}T00:00:00`);
  d.setDate(d.getDate() + n);
  return iso(d);
};

/** A JSON column arrives as an object here, and as a string from anything
    that stored it as text. Take either, trust neither. */
function asObject(raw) {
  let v = raw;
  if (typeof v === 'string') {
    try { v = JSON.parse(v); } catch { return {}; }
  }
  return (v && typeof v === 'object') ? v : {};
}

/* The finals, in the order they are played, which is the order they should
   read in. Keyed by stage to match what the settings form stores. */
const FINALS_STAGE_LABELS = [
  ['quarter-final', 'Quarter-final'],
  ['semi-final', 'Semi-final'],
  ['final', 'Final'],
];

/**
 * The days a competition is actually played: one per round, plus each
 * final that has been given a date.
 *
 * Not the start-to-end span. A club series that runs six weeks is played
 * on six Wednesdays, and colouring in all forty-two days says the club is
 * busy every day for a month and a half — which is both wrong and useless
 * for finding the free weekend.
 */
function playingDays(comp) {
  const roundTimes = asObject(comp.roundStartTimes);
  const finalsTimes = asObject(comp.finalsStartTimes);

  /* Rounds the competition still has. Shortening a competition leaves the
     dates of the rounds it dropped behind in roundStartDates — nothing
     deletes them — and reading every key put a round on the calendar months
     after the competition was over. */
  const roundCount = Number(comp.numberOfRounds);
  const rounds = Object.entries(asObject(comp.roundStartDates))
    .filter(([round]) => !roundCount || Number(round) <= roundCount)
    .map(([round, date]) => ({
      date: dayOnly(date),
      label: `R${round}`,
      time: roundTimes[round] || '',
      sort: Number(round) || 0,
    }));

  /* Stage keys, not round numbers: which finals a competition has depends
     on its format, and that can change after the dates are typed. */
  const finals = FINALS_STAGE_LABELS.map(([key, label], i) => ({
    date: dayOnly(asObject(comp.finalsStartDates)[key]),
    label,
    time: finalsTimes[key] || '',
    sort: 1000 + i,
  }));

  return [...rounds, ...finals]
    .filter(d => d.date)
    .sort((a, b) => a.sort - b.sort);
}

/**
 * Fold events and competitions into one map of ISO date -> marks.
 * Each mark carries both what a day cell shows and what the day's list
 * needs, so neither has to go back to the source record.
 */
function buildMarks(events, comps) {
  const map = new Map();
  const add = (date, mark) => {
    if (!date) return;
    if (!map.has(date)) map.set(date, []);
    map.get(date).push(mark);
  };

  events.forEach(ev => {
    const start = dayOnly(ev.startDate);
    if (!start) return;               // undated: nothing to put it next to

    /* A repeating event is marked on every night it is on. One record, many
       days: the club typed "Friday nights" once, and the calendar is the
       one place that has to know what that means. A one-off comes back from
       here as a single occurrence, so both take the same path. */
    const spanDays = datesBetween(start, dayOnly(ev.endDate)).length;

    occurrencesBetween(ev, start, seriesEnd(ev)).forEach(occurrence => {
      const last = spanDays > 1 ? addDaysISO(occurrence, spanDays - 1) : '';
      datesBetween(occurrence, last).forEach((date, i) => add(date, {
        key: `ev-${ev.id}-${date}`,
        kind: 'event',
        name: ev.name,
        category: ev.category || 'standard',
        dim: !ev.published || !!ev.cancelled,
        note: ev.cancelled ? 'Cancelled' : (!ev.published ? 'Not published' : ''),
        time: i === 0 ? (ev.startTime || '') : '',
        detail: [ev.kind, ev.location, ev.cost].filter(Boolean).join(' · '),
        continued: i > 0,
        stage: null,
      }));
    });
  });

  comps.forEach(c => {
    const dim = c.status === 'completed' || c.status === 'draft';
    const note = c.status === 'draft' ? 'Draft' : (c.status === 'completed' ? 'Finished' : '');
    const detail = [c.format, c.tournamentType].filter(Boolean).join(' · ');
    const days = playingDays(c);

    if (days.length) {
      /* Rounds played on the same day are one entry, not one per round. A
         mixer with four rounds on a Sunday filled the cell with four
         near-identical lines and pushed everything else in the month under
         "+1 more", when what the day is is one competition. */
      const byDate = new Map();
      days.forEach(d => {
        if (!byDate.has(d.date)) byDate.set(d.date, []);
        byDate.get(d.date).push(d);
      });

      /* A competition played over a weekend is on for the weekend. Champ
         Singles runs "9am Saturday 17th and 18th April" and only the first
         day carries a round date, so the Sunday came up empty — a member
         looking at the calendar for the 18th was told the green was free.
         A short run is filled in across its span; a six-week series is not,
         because it genuinely is not on for six weeks. */
      const spanStart = dayOnly(c.startDate);
      const spanEnd = dayOnly(c.endDate);
      const span = spanStart && spanEnd ? datesBetween(spanStart, spanEnd) : [];
      if (span.length > 1 && span.length <= CONTINUOUS_RUN_DAYS) {
        span.forEach(date => { if (!byDate.has(date)) byDate.set(date, []); });
      }

      /* In date order, so "continues" means what it says: a day filled in
         from the span is only a continuation if a playing day came before
         it. */
      const dates = [...byDate.keys()].sort();
      let started = false;
      dates.forEach(date => {
        const sameDay = byDate.get(date);
        const first = sameDay[0];
        const last = sameDay[sameDay.length - 1];
        const scheduled = sameDay.length > 0;
        add(date, {
          key: `comp-${c.id}-${date}`,
          kind: 'comp',
          name: c.name,
          dim,
          note,
          detail,
          /* The day starts when its first round does. */
          time: scheduled ? ((sameDay.find(d => d.time) || {}).time || '') : '',
          continued: !scheduled && started,
          /* A day inside the run with no round of its own says nothing about
             stages rather than inventing one. */
          stage: scheduled
            ? (sameDay.length > 1 ? `${first.label}–${last.label}` : first.label)
            : null,
        });
        if (scheduled) started = true;
      });
      return;
    }

    /* No round dates yet — a competition typed in with a start and an end
       and nothing scheduled. The span is all there is to say, so it is
       better than saying nothing, and it disappears the moment real dates
       are entered. */
    datesBetween(dayOnly(c.startDate), dayOnly(c.endDate)).forEach((date, i) => add(date, {
      key: `comp-${c.id}-${date}`,
      kind: 'comp',
      name: c.name,
      dim,
      note,
      detail,
      continued: i > 0,
      stage: null,
    }));
  });

  return map;
}

const markLabel = m => (m.stage ? `${m.name} — ${m.stage}` : m.name);

/** Days in a month, without a lookup table: day 0 of the next one. */
const daysInMonth = (year, monthIndex) => new Date(year, monthIndex + 1, 0).getDate();

/**
 * The planner: a row per month, columns aligned to the days of the week.
 *
 * A month can start on any of seven weekdays, so a row is offset by where
 * its first falls and the chart is 37 columns wide — six of slack plus the
 * longest month. That is the whole reason for the width: with every Monday
 * in one column and every weekend in the same two, a competition that runs
 * Saturdays is a straight line down the page, and the eye finds the free
 * weekend in a season without reading a single date.
 *
 * The cell shows the day of the month, since the column already says which
 * weekday it is, plus a bar for each kind of thing on that day. There is no
 * room for a name; that is what the day list underneath and the tooltip are
 * for.
 */
function YearPlanner({ holidays, marks, months, onOpenMonth, onPick, selected, today }) {
  return (
    <div className="cal-year-scroll">
      <div className="cal-year">
        <div className="cal-year-corner" />
        {WEEK_COLUMNS.map(c => (
          <div key={c} className={`cal-ycolhead${c % 7 >= 5 ? ' cal-ycol-weekend' : ''}`}>
            {DAY_INITIALS[c % 7]}
          </div>
        ))}

        {months.map(({ month: m, year }, row) => {
          /* Where this month's 1st sits in the week decides the whole row.
             Everything before it is empty, and so is everything past the
             month's last day. */
          const offset = mondayIndex(new Date(year, m, 1));
          const length = daysInMonth(year, m);
          const first = new Date(year, m, 1);
          /* The year is repeated only where it changes, so a Jul–Jun season
             says which July it started in without stamping "26" twelve
             times down the margin. */
          const showYear = row === 0 || m === 0;
          return (
            <React.Fragment key={`${year}-${m}`}>
              <button type="button" className="cal-ymonth" onClick={() => onOpenMonth(year, m)}
                title={`Show ${first.toLocaleDateString('en-NZ', { month: 'long', year: 'numeric' })} as a month`}>
                {first.toLocaleDateString('en-NZ', { month: 'short' })}
                {showYear && <span className="cal-ymonth-year">{String(year).slice(2)}</span>}
              </button>

              {WEEK_COLUMNS.map(c => {
                const n = c - offset + 1;
                if (n < 1 || n > length) {
                  return (
                    <div key={c} className={`cal-ycell cal-ycell-none${c % 7 >= 5 ? ' cal-ycol-weekend' : ''}`}
                      aria-hidden="true" />
                  );
                }
                const date = new Date(year, m, n);
                const key = iso(date);
                const dayMarks = marks.get(key) || [];
                const kinds = ['event', 'comp'].filter(k => dayMarks.some(x => x.kind === k));
                const holiday = holidays.get(key);
                const classes = [
                  'cal-ycell',
                  c % 7 >= 5 ? 'cal-ycell-weekend' : '',
                  holiday ? 'cal-ycell-holiday' : '',
                  dayMarks.length ? 'cal-ycell-busy' : '',
                  key === today ? 'cal-ycell-today' : '',
                  key === selected ? 'cal-ycell-on' : '',
                ].filter(Boolean).join(' ');
                const tip = [holiday, ...dayMarks.map(markLabel)].filter(Boolean).join(', ');
                return (
                  <button key={c} type="button" className={classes}
                    aria-current={key === today ? 'date' : undefined}
                    aria-pressed={key === selected}
                    title={tip || undefined}
                    aria-label={`${fmtLongDate(key)}${holiday ? `, ${holiday}` : ''}, ${dayMarks.length === 0 ? 'nothing on' : `${dayMarks.length} on`}`}
                    onClick={() => onPick(key)}>
                    <span className="cal-ydate">{n}</span>
                    <span className="cal-ybars">
                      {kinds.map(k => <span key={k} className={`cal-ybar cal-ybar-${k}`} />)}
                    </span>
                  </button>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function CalendarTab({ anniversaryRegion, clubId, onNewCompetition, showToast }) {
  const [events, setEvents] = React.useState([]);
  const [comps, setComps] = React.useState([]);
  const [filter, setFilter] = React.useState('all');
  const [view, setView] = React.useState('month');
  const [selected, setSelected] = React.useState(todayISO);
  const [draft, setDraft] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [month, setMonth] = React.useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  /* January to December of this year. The Jul – Jun preset is one click
     away for a club that plans by season. */
  const [rangeFrom, setRangeFrom] = React.useState(() => monthKey(new Date().getFullYear(), 0));
  const [rangeTo, setRangeTo] = React.useState(() => monthKey(new Date().getFullYear(), 11));

  const loadEvents = React.useCallback(() => {
    if (!clubId) { setEvents([]); return; }
    fetch(`${API}/api/events?clubId=${clubId}&all=1`)
      .then(r => (r.ok ? r.json() : []))
      .then(d => setEvents(Array.isArray(d) ? d : []))
      .catch(() => setEvents([]));
  }, [clubId]);

  React.useEffect(loadEvents, [loadEvents]);

  React.useEffect(() => {
    if (!clubId) { setComps([]); return; }
    fetch(`${API}/api/competitions?clubId=${clubId}`)
      .then(r => (r.ok ? r.json() : []))
      /* The server only honours ?clubId for a super-admin; everyone else gets
         their own club whatever they ask for. Filtering again here means the
         club picked in the footer is always the club shown. */
      .then(d => setComps(Array.isArray(d) ? d.filter(c => String(c.ClubId) === String(clubId)) : []))
      .catch(() => setComps([]));
  }, [clubId]);

  /* Picking a day while an event is half-written moves the event to that
     day rather than throwing the typing away. The form follows the day you
     are looking at, which is the only reading that matches what the section
     heading above it says. */
  const pickDay = key => {
    setSelected(key);
    setDraft(d => (d ? { ...d, startDate: key } : d));
  };

  const addEventOn = key => {
    setError('');
    setSelected(key);
    setDraft({ ...BLANK_EVENT, startDate: key });
  };

  const saveEvent = async () => {
    setError('');
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...draft, clubId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Could not save that.'); return; }
      /* Follow the event if its date was changed in the form, so the day
         list underneath is showing the thing that was just saved. */
      if (draft.startDate) setSelected(draft.startDate);
      setDraft(null);
      loadEvents();
      if (showToast) showToast('Event added.');
    } catch (e) {
      setError(e.message || 'Could not save that.');
    } finally {
      setSaving(false);
    }
  };

  const marks = React.useMemo(() => buildMarks(
    filter === 'comps' ? [] : events,
    filter === 'events' ? [] : comps,
  ), [events, comps, filter]);

  /* Whole weeks, Monday to Sunday, so the grid is always seven wide and the
     month sits inside the days either side of it rather than beside blanks. */
  const days = React.useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    const cursor = new Date(first);
    cursor.setDate(cursor.getDate() - mondayIndex(first));
    const out = [];
    /* Run to the end of the month, then on to the Sunday that finishes that
       week. Stopping at the last of the month would leave a short row. */
    while (cursor <= last || mondayIndex(cursor) !== 0) {
      out.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return out;
  }, [month]);

  /* The arrows step by whatever is on screen: a month in the month view, a
     year in the planner. Stepping a year one month at a time would take
     twelve clicks to change the only number the planner shows. */
  const plannerMonths = React.useMemo(
    () => monthsInRange(rangeFrom, rangeTo), [rangeFrom, rangeTo],
  );

  /* Only the years on screen. The month grid spills a few days into the
     year either side of it, which is why it asks for three. */
  const holidays = React.useMemo(() => {
    if (view === 'year' && plannerMonths.length) {
      return nzHolidayMap(plannerMonths[0].year, plannerMonths[plannerMonths.length - 1].year, anniversaryRegion);
    }
    const y = month.getFullYear();
    return nzHolidayMap(y - 1, y + 1, anniversaryRegion);
  }, [view, plannerMonths, month, anniversaryRegion]);

  /* The arrows move whatever is on screen: one month in the month view, one
     whole range in the planner. Stepping a Jul–Jun season by a single month
     would leave a chart that is no longer a season. */
  const shiftBy = n => {
    if (view === 'year') {
      const by = n * Math.max(plannerMonths.length, 1);
      setRangeFrom(f => shiftMonthKey(f, by));
      setRangeTo(t => shiftMonthKey(t, by));
    } else {
      setMonth(m => new Date(m.getFullYear(), m.getMonth() + n, 1));
    }
  };

  /* The two ranges every club asks for, as one click each. `startMonth` is
     the human month number, and the year chosen is whichever one makes the
     range the one we are currently in — in October, "Jul – Jun" means this
     July to next June; in March it means last July to this June. */
  const setSeasonRange = startMonth => {
    const n = new Date();
    const start = startMonth - 1;
    const year = n.getMonth() >= start ? n.getFullYear() : n.getFullYear() - 1;
    const from = monthKey(year, start);
    setRangeFrom(from);
    setRangeTo(shiftMonthKey(from, 11));
  };

  const goToday = () => {
    const n = new Date();
    setSelected(iso(n));
    setMonth(new Date(n.getFullYear(), n.getMonth(), 1));
    if (view !== 'year') return;
    /* Slide the range in whole range-lengths until today is inside it, so a
       season stays aligned to its season rather than being re-cut to start
       this month. */
    const length = Math.max(plannerMonths.length, 1);
    const now = n.getFullYear() * 12 + n.getMonth();
    const from = parseMonthKey(rangeFrom);
    if (!from) return;
    const start = from.year * 12 + from.month;
    const blocks = Math.floor((now - start) / length);
    if (blocks === 0) return;
    setRangeFrom(f => shiftMonthKey(f, blocks * length));
    setRangeTo(t => shiftMonthKey(t, blocks * length));
  };

  const monthLabel = view === 'year'
    ? [monthKeyLabel(plannerMonths.length ? monthKey(plannerMonths[0].year, plannerMonths[0].month) : rangeFrom),
      monthKeyLabel(plannerMonths.length
        ? monthKey(plannerMonths[plannerMonths.length - 1].year, plannerMonths[plannerMonths.length - 1].month)
        : rangeTo)].filter(Boolean).join(' – ')
    : month.toLocaleDateString('en-NZ', { month: 'long', year: 'numeric' });
  const selectedMarks = marks.get(selected) || [];
  const today = todayISO();

  if (!clubId) {
    return (
      <div className="gs-panel">
        <Section label="Club calendar">
          <p style={{ margin: 0, color: 'var(--gs-text-faint)' }}>Pick a club to see its calendar.</p>
        </Section>
      </div>
    );
  }

  return (
    <div className="gs-panel">
      <Section
        label="Club calendar"
        /* The range boxes belong with the arrows, not under the chart: they
           are the same control — where am I looking — and reading them a
           paragraph apart made the arrows look like the only way to move. */
        action={
          <div className="cal-toolbar">
            {view === 'year' && (
              <div className="cal-range">
                <Field label="From" htmlFor="cal-from">
                  <input id="cal-from" type="month" className="w3-input"
                    value={rangeFrom} onChange={e => setRangeFrom(e.target.value)} />
                </Field>
                <Field label="To" htmlFor="cal-to">
                  <input id="cal-to" type="month" className="w3-input"
                    value={rangeTo} onChange={e => setRangeTo(e.target.value)} />
                </Field>
                <div className="cal-range-presets">
                  <button type="button" className="w3-button w3-round poster-btn"
                    onClick={() => setSeasonRange(7)}>Jul – Jun</button>
                  <button type="button" className="w3-button w3-round poster-btn"
                    onClick={() => setSeasonRange(1)}>Jan – Dec</button>
                </div>
              </div>
            )}
            <div className="cal-nav">
              <button type="button" className="w3-button w3-round poster-btn"
                aria-label="Previous month" onClick={() => shiftBy(-1)}>&lsaquo;</button>
              <button type="button" className="w3-button w3-round poster-btn" onClick={goToday}>Today</button>
              <button type="button" className="w3-button w3-round poster-btn"
                aria-label="Next month" onClick={() => shiftBy(1)}>&rsaquo;</button>
            </div>
          </div>
        }>
        <div className="cal-head">
          <h3 className="cal-month">{monthLabel}</h3>
          <Segmented
            ariaLabel="Month or year"
            value={view}
            onChange={setView}
            options={[
              { value: 'month', label: 'Month' },
              { value: 'year', label: 'Year planner' },
            ]} />
          <Segmented
            ariaLabel="What to show on the calendar"
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all', label: 'Everything' },
              { value: 'events', label: 'Events' },
              { value: 'comps', label: 'Competitions' },
            ]} />
        </div>

        <p className="cal-intro">
          Events and competitions on one page. Click a day to see what is on it and to add
          something to it. Unpublished and cancelled events are dimmed rather than hidden, so a
          gap in the month is a real gap.
        </p>

        {view === 'year' && (
          <>
            {plannerMonths.length >= MAX_PLANNER_MONTHS && (
              <p className="cal-intro" style={{ marginTop: 0 }}>
                Two years at a time is the most it will chart.
              </p>
            )}

            <YearPlanner
              holidays={holidays}
              marks={marks}
              months={plannerMonths}
              selected={selected}
              today={today}
              onPick={pickDay}
              onOpenMonth={(y, m) => { setMonth(new Date(y, m, 1)); setView('month'); }} />
          </>
        )}

        {view === 'month' && (
        <div className="cal-grid">
          {DAY_NAMES.map(d => <div key={d} className="cal-dayname">{d}</div>)}

          {days.map(date => {
            const key = iso(date);
            const dayMarks = marks.get(key) || [];
            const holiday = holidays.get(key);
            const classes = [
              'cal-day',
              date.getMonth() !== month.getMonth() ? 'cal-day-outside' : '',
              holiday ? 'cal-day-holiday' : '',
              key === today ? 'cal-day-today' : '',
              key === selected ? 'cal-day-on' : '',
            ].filter(Boolean).join(' ');
            return (
              <button key={key} type="button" className={classes}
                aria-current={key === today ? 'date' : undefined}
                aria-pressed={key === selected}
                aria-label={`${fmtLongDate(key)}${holiday ? `, ${holiday}` : ''}, ${dayMarks.length === 0 ? 'nothing on' : `${dayMarks.length} on`}`}
                onClick={() => pickDay(key)}>
                <span className="cal-daynum">
                  {date.getDate()}
                  {holiday && <span className="cal-holiday" title={holiday}>{holiday}</span>}
                </span>
                <span className="cal-marks">
                  {dayMarks.slice(0, 3).map(m => (
                    <span key={m.key}
                      className={`cal-mark cal-mark-${m.kind}${m.dim ? ' cal-mark-dim' : ''}`}
                      title={markLabel(m)}>
                      {markLabel(m)}
                    </span>
                  ))}
                  {dayMarks.length > 3 && <span className="cal-more">+{dayMarks.length - 3} more</span>}
                </span>
              </button>
            );
          })}
        </div>
        )}
      </Section>

      <Section
        label={fmtLongDate(selected)}
        action={!draft && (
          <div className="cal-nav">
            <button type="button" className="w3-button w3-round poster-btn-primary"
              onClick={() => addEventOn(selected)}>Add an event</button>
            {onNewCompetition && (
              <button type="button" className="w3-button w3-round poster-btn"
                onClick={() => onNewCompetition(selected)}>New competition</button>
            )}
          </div>
        )}>
        {/* An event is small enough to write here — a name and a date is a
            valid one. A competition is not: it needs a format, a draw and a
            rules set before it means anything, so that button hands the day
            to the setup screen rather than pretending otherwise. */}
        {draft && (
          <EventForm value={draft} onChange={setDraft} onSave={saveEvent} saving={saving} error={error}
            onCancel={() => { setDraft(null); setError(''); }} />
        )}

        {/* Above the club's own things, because it is the reason some of
            them are there and the reason others should not be. */}
        {holidays.get(selected) && (
          <div className="cal-row">
            <span className="cal-dot cal-dot-holiday" aria-hidden="true" />
            <div className="cal-row-main">
              <div className="cal-row-name">{holidays.get(selected)}</div>
              <div className="cal-row-sub">New Zealand public holiday</div>
            </div>
          </div>
        )}

        {selectedMarks.length === 0 && !draft && (
          <p style={{ margin: 0, color: 'var(--gs-text-faint)' }}>
            {holidays.get(selected) ? 'Nothing on at the club this day.' : 'Nothing on this day.'}
          </p>
        )}
        {selectedMarks.map(m => (
          <div key={m.key} className={`cal-row${m.dim ? ' cal-row-dim' : ''}`}>
            <span className={`cal-dot cal-dot-${m.kind}`} aria-hidden="true" />
            <div className="cal-row-main">
              <div className="cal-row-name">
                {markLabel(m)}
                {m.category && m.category !== 'standard' && (
                  <span className={`event-flag event-flag-${m.category}`}>
                    {m.category === 'internal' ? 'Committee only' : 'Special'}
                  </span>
                )}
                {m.note && <span className="event-flag">{m.note}</span>}
                {m.continued && <span className="event-flag">Continues</span>}
              </div>
              <div className="cal-row-sub">
                {[m.kind === 'event' ? 'Event' : 'Competition', m.time, m.detail]
                  .filter(Boolean).join(' · ')}
              </div>
            </div>
          </div>
        ))}
      </Section>
    </div>
  );
}

export default CalendarTab;
export { buildMarks, datesBetween, monthsInRange, playingDays };
